import { TimePeriodType } from "@/models/timePeriod.model";
import {
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";

const today = new Date();
const todayRange = [today, today];
const yesterday = [subDays(today, 1), subDays(today, 1)];
const dayBeforeYesterday = [subDays(today, 2), subDays(today, 2)];
const thisWeek = [startOfWeek(today, { weekStartsOn: 1 }), today];
const lastWeek = [startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 })];
const thisMonth = [startOfMonth(today), today];
const lastMonth = [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))];
const previousWeekPeriod = [startOfWeek(subWeeks(today, 2), { weekStartsOn: 1 }), endOfWeek(subWeeks(today, 2), { weekStartsOn: 1 })];
const previousMonthPeriod = [startOfMonth(subMonths(today, 2)), endOfMonth(subMonths(today, 2))];
const partialLastWeek = [startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), subWeeks(today, 1)];
const partialLastMonth = [startOfMonth(subMonths(today, 1)), subMonths(today, 1)];

export const timePeriods = {
  today: todayRange,
  yesterday,
  thisWeek,
  lastWeek,
  thisMonth,
  lastMonth,
  custom: "Custom Period"
};

export const previousTimePeriods = {
  yesterday: yesterday,
  dayBeforeYesterday: dayBeforeYesterday,
  lastWeek: lastWeek,
  previousWeek: previousWeekPeriod,
  lastMonth: lastMonth,
  previousMonth: previousMonthPeriod,
  partialLastWeek,
  partialLastMonth,
  priorCustom: "Prior Custom Period"
};

export const convertToPriorPeriod = (date?: Date[]): Date[] => {
  if (!date) return [];
  const difference = differenceInCalendarDays(date[1], date[0]);
  return [subDays(date[0], difference + 1), subDays(date[0], 1)];
};

export const getDisplayDateCondensed = (startDate: Date, endDate: Date): string => {
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleString('en-US', { month: 'short' });

  return startMonth === endMonth
    ? `${startDay} - ${endDay} ${startMonth}`
    : `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
};

export const getDisplayDateRange = (
  startDate: Date, 
  endDate: Date | undefined | null, 
  monthFormat: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long' = 'short',
  includeYear: boolean = false
): string => {
  const startDay = startDate.getDate();
  const startMonth = startDate.toLocaleString('en-US', { month: monthFormat });
  const startYear = startDate.getFullYear();

  if (endDate) {
    const endDay = endDate?.getDate();
    const endMonth = endDate?.toLocaleString('en-US', { month: monthFormat });
    const endYear = endDate?.getFullYear();

    if (startYear !== endYear) {
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }
    if (startMonth !== endMonth) {
      return includeYear
        ? `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`
        : `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }
    if (startDay !== endDay) {
      return includeYear
        ? `${startDay} - ${endDay} ${startMonth} ${startYear}`
        : `${startDay} - ${endDay} ${startMonth}`;
    }
  }

  return includeYear
    ? `${startDay} ${startMonth} ${startYear}`
    : `${startDay} ${startMonth}`;
};

export const getMonthName = (monthNumber: number): string => {
  const date = new Date(2020, monthNumber);
  return date.toLocaleString("default", { month: "long" });
};

export const getTimePeriodTypeDateRange = (
  timePeriodType: TimePeriodType,
  startDate: Date = new Date()
): [Date, Date] | undefined => {
  const normalizedStartDate = startOfDay(new Date(startDate));

  switch (timePeriodType) {
    case TimePeriodType.today:
      return [normalizedStartDate, normalizedStartDate];
    case TimePeriodType.tomorrow:
      const tomorrow = new Date(normalizedStartDate);
      tomorrow.setDate(normalizedStartDate.getDate() + 1);
      return [tomorrow, tomorrow];
    case TimePeriodType.yesterday:
      const yesterday = new Date(normalizedStartDate);
      yesterday.setDate(normalizedStartDate.getDate() - 1);
      return [yesterday, yesterday];
    case TimePeriodType.thisWeek:
      return [startOfWeek(normalizedStartDate, { weekStartsOn: 1 }), normalizedStartDate];
    case TimePeriodType.lastWeek:
      const lastWeekStart = startOfWeek(subWeeks(normalizedStartDate, 1), { weekStartsOn: 1 });
      return [lastWeekStart, startOfDay(endOfWeek(lastWeekStart, { weekStartsOn: 1 }))];
    case TimePeriodType.thisMonth:
      return [startOfMonth(normalizedStartDate), normalizedStartDate];
    case TimePeriodType.lastMonth:
      const lastMonthStart = startOfMonth(subMonths(normalizedStartDate, 1));
      return [lastMonthStart, startOfDay(endOfMonth(lastMonthStart))];
    case TimePeriodType.fourWeeks:
      const fourWeeksEnd = new Date(normalizedStartDate);
      fourWeeksEnd.setDate(normalizedStartDate.getDate() + 28);
      return [normalizedStartDate, startOfDay(fourWeeksEnd)];
    case TimePeriodType.sixMonths:
      const sixMonthsEnd = new Date(normalizedStartDate);
      sixMonthsEnd.setMonth(normalizedStartDate.getMonth() + 6);
      return [normalizedStartDate, startOfDay(sixMonthsEnd)];
    case TimePeriodType.selectDate:
    case TimePeriodType.custom:
    case TimePeriodType.noEndDate:
    default:
      return undefined;
  }
};

export const getTimePeriodTypeDate = (
  timePeriodType: TimePeriodType,
  startDate: Date = new Date()
): Date | undefined => {

  const today = startOfDay(new Date());
  const normalizedStartDate = startOfDay(new Date(startDate));

  let newDate: Date | undefined = undefined;

  switch (timePeriodType) {
    case TimePeriodType.today:
      newDate = today;
      break;
    case TimePeriodType.yesterday:
      newDate = new Date(today);
      newDate.setDate(today.getDate() - 1);
      break;
    case TimePeriodType.tomorrow:
      newDate = new Date(today);
      newDate.setDate(today.getDate() + 1);
      break;
    case TimePeriodType.thisWeek:
      newDate = startOfWeek(today);
      break;
    case TimePeriodType.lastWeek:
      newDate = startOfWeek(subWeeks(today, 1));
      break;
    case TimePeriodType.thisMonth:
      newDate = startOfMonth(today);
      break;
    case TimePeriodType.lastMonth:
      newDate = startOfMonth(subMonths(today, 1));
      break;
    case TimePeriodType.fourWeeks:
      newDate = new Date(normalizedStartDate);
      newDate.setDate(normalizedStartDate.getDate() + 28);
      break;
    case TimePeriodType.sixMonths:
      newDate = new Date(normalizedStartDate);
      newDate.setMonth(normalizedStartDate.getMonth() + 6);
      break;
    case TimePeriodType.selectDate:
    case TimePeriodType.custom:
    case TimePeriodType.noEndDate:
    default:
      break
  }

  return newDate;
};

export const getTimePeriodTypeByDateRange = (
  startDate: Date,
  endDate: Date,
  defaultTimePeriodType: TimePeriodType = TimePeriodType.custom
): TimePeriodType => {
  const normalizedStartDate = startOfDay(new Date(startDate));
  const normalizedEndDate = startOfDay(new Date(endDate));
  const today = startOfDay(new Date());

  const isSameRange = (range: [Date, Date] | undefined): boolean => {
    if (!range) return false;

    return startOfDay(range[0]).getTime() === normalizedStartDate.getTime()
      && startOfDay(range[1]).getTime() === normalizedEndDate.getTime();
  };

  const currentDateRanges: Array<[TimePeriodType, [Date, Date] | undefined]> = [
    [TimePeriodType.today, getTimePeriodTypeDateRange(TimePeriodType.today, today)],
    [TimePeriodType.tomorrow, getTimePeriodTypeDateRange(TimePeriodType.tomorrow, today)],
    [TimePeriodType.yesterday, getTimePeriodTypeDateRange(TimePeriodType.yesterday, today)],
    [TimePeriodType.thisWeek, getTimePeriodTypeDateRange(TimePeriodType.thisWeek, today)],
    [TimePeriodType.lastWeek, getTimePeriodTypeDateRange(TimePeriodType.lastWeek, today)],
    [TimePeriodType.thisMonth, getTimePeriodTypeDateRange(TimePeriodType.thisMonth, today)],
    [TimePeriodType.lastMonth, getTimePeriodTypeDateRange(TimePeriodType.lastMonth, today)],
    [TimePeriodType.fourWeeks, getTimePeriodTypeDateRange(TimePeriodType.fourWeeks, normalizedStartDate)],
    [TimePeriodType.sixMonths, getTimePeriodTypeDateRange(TimePeriodType.sixMonths, normalizedStartDate)],
  ];

  for (const [timePeriodType, range] of currentDateRanges) {
    if (isSameRange(range)) {
      return timePeriodType;
    }
  }

  return defaultTimePeriodType;
};

export const getTimePeriodTypeBySingleDate = (
  selectedDate: Date,
  startDate: Date = new Date(),
  defaultTimePeriodType: TimePeriodType = TimePeriodType.custom
): TimePeriodType => {
  const normalizedSelectedDate = startOfDay(new Date(selectedDate)).getTime();

  const today = startOfDay(new Date());

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const thisWeekStart = startOfWeek(today);
  const lastWeekStart = startOfWeek(subWeeks(today, 1));
  const thisMonthStart = startOfMonth(today);
  const lastMonthStart = startOfMonth(subMonths(today, 1));

  const fourWeeks = startOfDay(new Date(startDate));
  fourWeeks.setDate(startDate.getDate() + 28);

  const sixMonths = startOfDay(new Date(startDate));
  sixMonths.setMonth(startDate.getMonth() + 6);

  const timePeriodTypesMapping: Array<[TimePeriodType, Date]> = [
    [TimePeriodType.today, today],
    [TimePeriodType.yesterday, yesterday],
    [TimePeriodType.tomorrow, tomorrow],
    [TimePeriodType.thisWeek, thisWeekStart],
    [TimePeriodType.lastWeek, lastWeekStart],
    [TimePeriodType.thisMonth, thisMonthStart],
    [TimePeriodType.lastMonth, lastMonthStart],
    [TimePeriodType.fourWeeks, fourWeeks],
    [TimePeriodType.sixMonths, sixMonths],
  ];

  for (const [timePeriodType, date] of timePeriodTypesMapping) {
    if (normalizedSelectedDate === date.getTime()) {
      return timePeriodType;
    }
  }

  return defaultTimePeriodType;
};